import { useEffect, useState } from 'react'
import { useFit } from '@helpers/hooks/useFit'
import { analytics } from '@helpers/analytics'
import styles from './styles.module.scss'
import Note from '@components/ui/Note'
import HelpButton from '@components/ui/HelpButton'
import { Fit } from 'types/fit'
import { FIT_FINDER_HASH } from '@consts/index'
import useIsReactClient from '@helpers/hooks/useIsReactClient'

type SavedFitBoxProps = {
  optionTypesToShow: string[]
  appliedFilterOptions: {
    type: string
    value: string
  }[]
  variant?: 'dot' | 'border'
}

function SavedFitBox({
  optionTypesToShow = [],
  appliedFilterOptions = [],
  variant = 'border'
}: SavedFitBoxProps) {
  const [isNoteVisible, setIsNoteVisible] = useState(false)
  const isReactClient = useIsReactClient()
  const { savedFit } = useFit()
  const fit = savedFit || ({} as Fit)

  const optionTypesToRender = isReactClient
    ? optionTypesToShow.filter(type => fit[type as keyof Fit])
    : []

  const isSavedFitApplied =
    !!appliedFilterOptions.length &&
    appliedFilterOptions
      .filter(option => optionTypesToRender.includes(option.type))
      .every(
        option => String(option.value) === String(fit[option.type as keyof Fit])
      )

  const hasAtLeastTwoOptionsToRender = optionTypesToRender.length > 1

  const handleNoteToggle = () => {
    const noteNewState = !isNoteVisible
    setIsNoteVisible(noteNewState)
    analytics('Click', {
      category: 'Saved Fit Box',
      label: noteNewState
        ? 'Saved Fit tooltip - open'
        : 'Saved Fit tooltip - close'
    })
  }

  useEffect(() => {
    if (hasAtLeastTwoOptionsToRender) {
      analytics('Saved Fit Displayed')
    }
  }, [hasAtLeastTwoOptionsToRender])

  if (!hasAtLeastTwoOptionsToRender) {
    return null
  }

  return (
    <>
      <div
        className={`${styles.SavedFitBox} ${styles[`variant_${variant}`]} ${
          isSavedFitApplied ? styles.isApplied : ''
        }`}
      >
        <p className={styles.SavedFitBox__title}>
          Your Fit{' '}
          <HelpButton
            active={isNoteVisible}
            onClick={handleNoteToggle}
            classes={styles.SavedFitBox__helperButton}
          />
        </p>

        <div className={styles.SavedFitBox__optionsContainer}>
          <div className={styles.SavedFitBox__options}>
            {optionTypesToRender.map(type => (
              <div className={styles.SavedFitBox__option} key={type}>
                <span className={styles.SavedFitBox__optionType}>{type}</span>
                <span className={styles.SavedFitBox__optionValue}>
                  {fit[type as keyof Fit]}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {isNoteVisible && (
        <Note classes={styles.SavedFitBox__note}>
          You will see your recommended Fit throughout your shopping experience.
          If you need to tweak your size{' '}
          <a href={`#${FIT_FINDER_HASH}`} title="Go to Fit finder">
            click here
          </a>
          .
        </Note>
      )}
    </>
  )
}

export default SavedFitBox
